import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useDemo } from "../hooks/useDemo";
import { TourOverlay } from "./TourOverlay";
import {
  TOUR_STEPS,
  SCHEDULE_DETAIL_TOUR_STEPS,
  OPTIMIZATION_TOUR_STEPS,
  CALENDAR_TOUR_STEPS,
  TEACHERS_TOUR_STEPS,
  TEACHERS_CALENDAR_TOUR_STEPS,
  ACCEPTANCE_TOUR_STEPS,
  PARENT_APPROVALS_TOUR_STEPS,
  APPROVAL_DETAIL_TOUR_STEPS,
  FINALIZED_TOUR_STEPS,
  FINALIZATION_PAGE_TOUR_STEPS,
  LOCATION_DETAIL_TOUR_STEPS,
  DASHBOARD_TOUR_STEPS,
  DICTIONARY_CONTEXTS_TOUR_STEPS,
  DICTIONARY_LOCATIONS_TOUR_STEPS,
  DICTIONARY_TEACHERS_TOUR_STEPS,
  DICTIONARY_TRANSPORT_TOUR_STEPS,
  EMPLOYEES_TOUR_STEPS,
  REPORTS_TOUR_STEPS,
  SUBSTITUTIONS_REPORT_TOUR_STEPS,
  WORKLOAD_REPORT_TOUR_STEPS,
  COMMUNICATION_REPORT_TOUR_STEPS,
  SUBSTITUTIONS_TOUR_STEPS,
  DEPARTURES_TAB_TOUR_STEPS,
  SUBSTITUTION_DETAIL_TOUR_STEPS,
  DEPARTURE_DETAIL_TOUR_STEPS,
  TIMESHEET_TOUR_STEPS,
  TIMESHEET_CALENDAR_TOUR_STEPS,
  CHAT_TOUR_STEPS,
  CHAT_GROUP_TOUR_STEPS,
  type TourStep,
} from "../data/tourSteps";

const TOUR_KEYS = {
  dashboard: "demo_tour_dashboard_completed",
  scheduleList: "demo_tour_completed",
  scheduleDetail: "demo_tour_schedule_detail_completed",
  optimizationDetail: "demo_tour_optimization_detail_completed",
  calendar: "demo_tour_calendar_completed",
  teachersDetail: "demo_tour_teachers_detail_completed",
  teachersCalendar: "demo_tour_teachers_calendar_completed",
  acceptanceDetail: "demo_tour_acceptance_detail_completed",
  parentApprovals: "demo_tour_parent_approvals_completed",
  approvalDetail: "demo_tour_approval_detail_completed",
  finalizedDetail: "demo_tour_finalized_detail_completed",
  finalizationPage: "demo_tour_finalization_page_completed",
  locationDetail: "demo_tour_location_detail_completed",
  dictionaryContexts: "demo_tour_dictionary_contexts_completed",
  dictionaryLocations: "demo_tour_dictionary_locations_completed",
  dictionaryTeachers: "demo_tour_dictionary_teachers_completed",
  dictionaryTransport: "demo_tour_dictionary_transport_completed",
  employees: "demo_tour_employees_completed",
  reports: "demo_tour_reports_completed",
  reportsSubstitutions: "demo_tour_reports_substitutions_completed",
  reportsWorkload: "demo_tour_reports_workload_completed",
  reportsCommunication: "demo_tour_reports_communication_completed",
  substitutions: "demo_tour_substitutions_completed",
  departuresTab: "demo_tour_departures_tab_completed",
  substitutionDetail: "demo_tour_substitution_detail_completed",
  departureDetail: "demo_tour_departure_detail_completed",
  timesheet: "demo_tour_timesheet_completed",
  timesheetCalendar: "demo_tour_timesheet_calendar_completed",
  chat: "demo_tour_chat_completed",
  chatGroup: "demo_tour_chat_group_completed",
} as const;

interface TourConfig {
  steps: TourStep[];
  storageKey: string;
}

function detectLocationDetailVisible(): boolean {
  return !!document.querySelector('[data-tour="add-room-btn"]');
}

function detectDeparturesTabVisible(): boolean {
  const el = document.querySelector('[data-tour="departures-filters"]') as HTMLElement | null;
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function detectTimesheetCalendarVisible(): boolean {
  const els = document.querySelectorAll('[data-tour="timesheet-calendar-grid"]');
  for (const el of els) {
    const rect = (el as HTMLElement).getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return true;
  }
  return false;
}

function getTourConfig(
  pathname: string,
  isLocationDetailVisible: boolean,
  isDeparturesTabVisible: boolean,
  isTimesheetCalendarVisible: boolean
): TourConfig | null {
  // Widok kalendarza demo-schedule-002 (etap przypisania prowadzących)
  if (pathname === "/schedules/demo-schedule-002/calendar") {
    return {
      steps: TEACHERS_CALENDAR_TOUR_STEPS,
      storageKey: TOUR_KEYS.teachersCalendar,
    };
  }

  // Widok kalendarza demo-schedule-001
  if (pathname === "/schedules/demo-schedule-001/calendar") {
    return {
      steps: CALENDAR_TOUR_STEPS,
      storageKey: TOUR_KEYS.calendar,
    };
  }

  // Widok lokalizacji w szczegółach demo-schedule-003
  if (pathname === "/schedules/demo-schedule-003" && isLocationDetailVisible) {
    return {
      steps: LOCATION_DETAIL_TOUR_STEPS,
      storageKey: TOUR_KEYS.locationDetail,
    };
  }

  // Szczegóły demo-schedule-003 (etap setup)
  if (pathname === "/schedules/demo-schedule-003") {
    return {
      steps: SCHEDULE_DETAIL_TOUR_STEPS,
      storageKey: TOUR_KEYS.scheduleDetail,
    };
  }

  // Strona finalizacji demo-schedule-005
  if (pathname === "/schedules/demo-schedule-005/finalization") {
    return {
      steps: FINALIZATION_PAGE_TOUR_STEPS,
      storageKey: TOUR_KEYS.finalizationPage,
    };
  }

  // Szczegóły demo-schedule-005 (etap finalizacji)
  if (pathname === "/schedules/demo-schedule-005") {
    return {
      steps: FINALIZED_TOUR_STEPS,
      storageKey: TOUR_KEYS.finalizedDetail,
    };
  }

  // Szczegóły procesu akceptacji demo-schedule-004
  if (pathname.startsWith("/schedules/demo-schedule-004/parent-approvals/")) {
    return {
      steps: APPROVAL_DETAIL_TOUR_STEPS,
      storageKey: TOUR_KEYS.approvalDetail,
    };
  }

  // Strona akceptacji rodziców demo-schedule-004
  if (pathname === "/schedules/demo-schedule-004/parent-approvals") {
    return {
      steps: PARENT_APPROVALS_TOUR_STEPS,
      storageKey: TOUR_KEYS.parentApprovals,
    };
  }

  // Szczegóły demo-schedule-004 (etap akceptacji rodziców)
  if (pathname === "/schedules/demo-schedule-004") {
    return {
      steps: ACCEPTANCE_TOUR_STEPS,
      storageKey: TOUR_KEYS.acceptanceDetail,
    };
  }

  // Szczegóły demo-schedule-002 (etap przypisania prowadzących)
  if (pathname === "/schedules/demo-schedule-002") {
    return {
      steps: TEACHERS_TOUR_STEPS,
      storageKey: TOUR_KEYS.teachersDetail,
    };
  }

  // Szczegóły demo-schedule-001 (etap optymalizacji)
  if (pathname === "/schedules/demo-schedule-001") {
    return {
      steps: OPTIMIZATION_TOUR_STEPS,
      storageKey: TOUR_KEYS.optimizationDetail,
    };
  }

  // Lista grafików
  if (pathname === "/schedules") {
    return {
      steps: TOUR_STEPS,
      storageKey: TOUR_KEYS.scheduleList,
    };
  }

  // Bank danych - konteksty
  if (pathname === "/dictionary-data/contexts" || pathname === "/dictionary-data") {
    return {
      steps: DICTIONARY_CONTEXTS_TOUR_STEPS,
      storageKey: TOUR_KEYS.dictionaryContexts,
    };
  }

  // Bank danych - lokalizacje
  if (pathname === "/dictionary-data/locations") {
    return {
      steps: DICTIONARY_LOCATIONS_TOUR_STEPS,
      storageKey: TOUR_KEYS.dictionaryLocations,
    };
  }

  // Bank danych - prowadzący
  if (pathname === "/dictionary-data/teachers") {
    return {
      steps: DICTIONARY_TEACHERS_TOUR_STEPS,
      storageKey: TOUR_KEYS.dictionaryTeachers,
    };
  }

  // Bank danych - czasy transportu
  if (pathname === "/dictionary-data/transport-times") {
    return {
      steps: DICTIONARY_TRANSPORT_TOUR_STEPS,
      storageKey: TOUR_KEYS.dictionaryTransport,
    };
  }

  // Pracownicy
  if (pathname === "/employees") {
    return {
      steps: EMPLOYEES_TOUR_STEPS,
      storageKey: TOUR_KEYS.employees,
    };
  }

  // Zastępstwa - zakładka Odejścia (wykryta przez widoczność w DOM)
  if (pathname === "/substitutions" && isDeparturesTabVisible) {
    return {
      steps: DEPARTURES_TAB_TOUR_STEPS,
      storageKey: TOUR_KEYS.departuresTab,
    };
  }

  // Zastępstwa - zakładka Zastępstwa (domyślna)
  if (pathname === "/substitutions") {
    return {
      steps: SUBSTITUTIONS_TOUR_STEPS,
      storageKey: TOUR_KEYS.substitutions,
    };
  }

  // Szczegóły odejścia (musi być PRZED szczegółami zgłoszenia, żeby nie zostały złapane)
  if (pathname.startsWith("/substitutions/departures/")) {
    return {
      steps: DEPARTURE_DETAIL_TOUR_STEPS,
      storageKey: TOUR_KEYS.departureDetail,
    };
  }

  // Szczegóły zgłoszenia
  if (pathname.startsWith("/substitutions/")) {
    return {
      steps: SUBSTITUTION_DETAIL_TOUR_STEPS,
      storageKey: TOUR_KEYS.substitutionDetail,
    };
  }

  // Czat - lista grup (nie szczegóły grupy)
  if (pathname === "/chat") {
    return {
      steps: CHAT_TOUR_STEPS,
      storageKey: TOUR_KEYS.chat,
    };
  }

  // Czat - szczegóły konkretnej grupy
  if (pathname.startsWith("/chat/")) {
    return {
      steps: CHAT_GROUP_TOUR_STEPS,
      storageKey: TOUR_KEYS.chatGroup,
    };
  }

  // Czas pracy - tryb Kalendarz (wykryty przez widoczność siatki)
  if (pathname === "/timesheet" && isTimesheetCalendarVisible) {
    return {
      steps: TIMESHEET_CALENDAR_TOUR_STEPS,
      storageKey: TOUR_KEYS.timesheetCalendar,
    };
  }

  // Czas pracy - tryb Lista (domyślny)
  if (pathname === "/timesheet") {
    return {
      steps: TIMESHEET_TOUR_STEPS,
      storageKey: TOUR_KEYS.timesheet,
    };
  }

  // Raporty - zakładka Zastępstwa
  if (pathname === "/reports/substitutions") {
    return {
      steps: SUBSTITUTIONS_REPORT_TOUR_STEPS,
      storageKey: TOUR_KEYS.reportsSubstitutions,
    };
  }

  // Raporty - zakładka Obciążenie
  if (pathname === "/reports/workload") {
    return {
      steps: WORKLOAD_REPORT_TOUR_STEPS,
      storageKey: TOUR_KEYS.reportsWorkload,
    };
  }

  // Raporty - zakładka Komunikacja
  if (pathname === "/reports/communication") {
    return {
      steps: COMMUNICATION_REPORT_TOUR_STEPS,
      storageKey: TOUR_KEYS.reportsCommunication,
    };
  }

  // Raporty - pozostałe zakładki (domyślna Czas pracy)
  if (pathname === "/reports" || pathname === "/reports/work-time") {
    return {
      steps: REPORTS_TOUR_STEPS,
      storageKey: TOUR_KEYS.reports,
    };
  }

  // Dashboard
  if (pathname === "/dashboard") {
    return {
      steps: DASHBOARD_TOUR_STEPS,
      storageKey: TOUR_KEYS.dashboard,
    };
  }

  return null;
}

export function DemoGuidedTour() {
  const { isDemoMode } = useDemo();
  const location = useLocation();
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isLocationDetailVisible, setIsLocationDetailVisible] = useState(false);
  const [isDeparturesTabVisible, setIsDeparturesTabVisible] = useState(false);
  const [isTimesheetCalendarVisible, setIsTimesheetCalendarVisible] = useState(false);

  // Wykrywanie widoku lokalizacji przez obecność elementu DOM
  useEffect(() => {
    if (!isDemoMode || location.pathname !== "/schedules/demo-schedule-003") {
      setIsLocationDetailVisible(false);
      return;
    }

    // Sprawdź po krótkim opóźnieniu (żeby DOM się załadował)
    const timer = setTimeout(() => {
      setIsLocationDetailVisible(detectLocationDetailVisible());
    }, 500);

    // Obserwuj zmiany DOM (przejście do/z widoku lokalizacji)
    const observer = new MutationObserver(() => {
      setIsLocationDetailVisible(detectLocationDetailVisible());
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [isDemoMode, location.pathname]);

  // Wykrywanie zakładki Odejścia na /substitutions (zakładki sterowane stanem, nie URL)
  useEffect(() => {
    if (!isDemoMode || location.pathname !== "/substitutions") {
      setIsDeparturesTabVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsDeparturesTabVisible(detectDeparturesTabVisible());
    }, 500);

    const observer = new MutationObserver(() => {
      setIsDeparturesTabVisible(detectDeparturesTabVisible());
    });

    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [isDemoMode, location.pathname]);

  // Wykrywanie trybu Kalendarza w /timesheet (viewMode sterowany stanem komponentu)
  useEffect(() => {
    if (!isDemoMode || location.pathname !== "/timesheet") {
      setIsTimesheetCalendarVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsTimesheetCalendarVisible(detectTimesheetCalendarVisible());
    }, 500);

    const observer = new MutationObserver(() => {
      setIsTimesheetCalendarVisible(detectTimesheetCalendarVisible());
    });

    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [isDemoMode, location.pathname]);

  const tourConfig = useMemo(
    () => getTourConfig(location.pathname, isLocationDetailVisible, isDeparturesTabVisible, isTimesheetCalendarVisible),
    [location.pathname, isLocationDetailVisible, isDeparturesTabVisible, isTimesheetCalendarVisible]
  );

  useEffect(() => {
    if (isDemoMode && tourConfig && !localStorage.getItem(tourConfig.storageKey)) {
      const timer = setTimeout(() => {
        setShowTour(true);
        setCurrentStep(0);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setShowTour(false);
      setCurrentStep(0);
    }
  }, [isDemoMode, tourConfig]);

  const handleComplete = () => {
    if (tourConfig) {
      localStorage.setItem(tourConfig.storageKey, "true");
    }
    setShowTour(false);
    setCurrentStep(0);
  };

  if (!showTour || !tourConfig) return null;

  return (
    <TourOverlay
      steps={tourConfig.steps}
      currentStep={currentStep}
      onNext={() => setCurrentStep((prev) => prev + 1)}
      onPrev={() => setCurrentStep((prev) => prev - 1)}
      onSkip={handleComplete}
      onComplete={handleComplete}
    />
  );
}

/** Restarts the guided tour by clearing all localStorage flags */
// eslint-disable-next-line react-refresh/only-export-components
export function restartTour() {
  Object.values(TOUR_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });
}
